import React, { useCallback, useEffect, useState } from 'react';
import { AlertCircle, Cloud, CloudFog, CloudLightning, CloudRain, CloudSun, Droplets, MapPin, RefreshCw, Snowflake, Sun, Umbrella, Wind } from 'lucide-react';
import { Card, CardContent, CardHeader } from './ui/card';
import { Skeleton } from './ui/skeleton';
import { getBrooklineWeather } from '../lib/api';

const toFahrenheit = (value) => value == null ? null : Math.round((value * 9) / 5 + 32);
const valueOrDash = (value, suffix = '') => value == null ? '—' : `${Math.round(value)}${suffix}`;

const iconFor = (code) => {
  if (code === 0) return Sun;
  if ([1, 2].includes(code)) return CloudSun;
  if (code === 3) return Cloud;
  if ([45, 48].includes(code)) return CloudFog;
  if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) return CloudRain;
  if ([71, 73, 75, 77, 85, 86].includes(code)) return Snowflake;
  if ([95, 96, 99].includes(code)) return CloudLightning;
  return CloudSun;
};

export const BrooklineWeather = () => {
  const [weather, setWeather] = useState(null);
  const [status, setStatus] = useState('loading');

  const load = useCallback(async () => {
    setStatus('loading');
    try {
      setWeather(await getBrooklineWeather());
      setStatus('ready');
    } catch {
      setStatus('error');
    }
  }, []);

  useEffect(() => {
    load();
    const timer = window.setInterval(load, 30 * 60 * 1000);
    return () => window.clearInterval(timer);
  }, [load]);

  if (status === 'loading') return (
    <Card className="rounded-none border-0 bg-[#0a1f44] text-white shadow-none" data-testid="brookline-weather-loading">
      <CardHeader><Skeleton className="h-5 w-40 bg-white/20" /></CardHeader>
      <CardContent className="space-y-4"><Skeleton className="h-16 w-28 bg-white/20" /><Skeleton className="h-24 w-full bg-white/15" /></CardContent>
    </Card>
  );

  if (status === 'error' || !weather) return (
    <Card className="rounded-none border-2 border-[#0a1f44] bg-white shadow-none" data-testid="brookline-weather-error">
      <CardContent className="p-7"><AlertCircle className="w-7 h-7 text-[#c8102e] mb-3" /><div className="font-display text-2xl text-[#0a1f44]">WEATHER UNAVAILABLE</div><p className="text-sm text-[#0a1f44]/65 mt-2">Brookline conditions could not be loaded right now.</p><button type="button" onClick={load} data-testid="brookline-weather-retry" className="inline-flex items-center gap-2 mt-5 border border-[#0a1f44] px-4 py-2 font-heading uppercase tracking-widest text-xs text-[#0a1f44] hover:bg-[#0a1f44] hover:text-white transition-colors"><RefreshCw className="w-4 h-4" /> Retry</button></CardContent>
    </Card>
  );

  const WeatherIcon = iconFor(weather.current.weather_code);
  const temperature = toFahrenheit(weather.current.temperature_c);
  const feelsLike = toFahrenheit(weather.current.apparent_temperature_c);
  const high = toFahrenheit(weather.today.temperature_max_c);
  const low = toFahrenheit(weather.today.temperature_min_c);
  const windMph = weather.current.wind_speed_kmh == null ? null : weather.current.wind_speed_kmh * 0.621371;
  const updated = weather.current.time?.split('T')[1] || '';

  return (
    <Card className="rounded-none border-0 bg-[#0a1f44] text-white shadow-none overflow-hidden" data-testid="brookline-weather-widget" aria-label="Today’s Brookline weather">
      <div className="h-1 bg-[#c8102e]" />
      <CardHeader className="pb-4">
        <div className="flex items-start justify-between gap-4"><div><div className="font-heading uppercase tracking-[0.28em] text-[10px] text-white/60">Today in Brookline</div><div className="font-display text-3xl mt-1">GAME-DAY WEATHER</div></div><WeatherIcon className="w-10 h-10 text-white" aria-hidden="true" /></div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-end gap-4 border-b border-white/15 pb-6"><div className="font-display text-6xl leading-none" data-testid="brookline-weather-temperature">{temperature == null ? '—' : `${temperature}°`}</div><div className="pb-1"><div className="font-heading uppercase text-sm" data-testid="brookline-weather-condition">{weather.current.condition}</div><div className="text-xs text-white/60 mt-1">Feels like {feelsLike == null ? '—' : `${feelsLike}°F`}</div></div></div>
        <div className="grid grid-cols-2 gap-px bg-white/15 mt-6">
          <div className="bg-[#0a1f44] py-3 pr-3"><div className="text-[10px] uppercase font-heading tracking-widest text-white/50">High / Low</div><div className="font-heading mt-1" data-testid="brookline-weather-high-low">{high == null ? '—' : `${high}°`} / {low == null ? '—' : `${low}°`}</div></div>
          <div className="bg-[#0a1f44] py-3 pl-3"><div className="text-[10px] uppercase font-heading tracking-widest text-white/50 flex items-center gap-1"><Umbrella className="w-3 h-3" /> Rain</div><div className="font-heading mt-1" data-testid="brookline-weather-rain">{valueOrDash(weather.today.precipitation_probability_max_percent, '%')}</div></div>
          <div className="bg-[#0a1f44] py-3 pr-3"><div className="text-[10px] uppercase font-heading tracking-widest text-white/50 flex items-center gap-1"><Droplets className="w-3 h-3" /> Humidity</div><div className="font-heading mt-1" data-testid="brookline-weather-humidity">{valueOrDash(weather.current.relative_humidity_percent, '%')}</div></div>
          <div className="bg-[#0a1f44] py-3 pl-3"><div className="text-[10px] uppercase font-heading tracking-widest text-white/50 flex items-center gap-1"><Wind className="w-3 h-3" /> Wind</div><div className="font-heading mt-1" data-testid="brookline-weather-wind">{valueOrDash(windMph, ' mph')}</div></div>
        </div>
        <div className="flex items-center gap-2 text-xs text-white/55 mt-6" data-testid="brookline-weather-location"><MapPin className="w-3 h-3 text-[#c8102e]" /> {weather.location}</div>
        <div className="text-[10px] text-white/45 mt-2">Updated {updated || 'today'} · <a href="https://open-meteo.com/" target="_blank" rel="noreferrer" data-testid="brookline-weather-attribution" className="underline hover:text-white transition-colors">Weather data by Open-Meteo</a></div>
      </CardContent>
    </Card>
  );
};